import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { QRCodeCanvas } from "qrcode.react";
import { api } from "@/lib/api";
import * as Lucide from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";

const isMobileDevice = () => {
  if (typeof window === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(ua)) return true;
  return window.matchMedia?.("(pointer: coarse)").matches && window.innerWidth < 900;
};

function Countdown({ expiresAt }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const ms = Math.max(0, new Date(expiresAt).getTime() - now);
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  const s = Math.floor((ms % 60_000) / 1000);
  const urgent = ms < 60 * 60 * 1000;
  return (
    <span data-testid="paylink-countdown" className={`bx-mono text-xs ${urgent ? "text-amber-300" : "text-slate-400"}`}>
      {h.toString().padStart(2, "0")}:{m.toString().padStart(2, "0")}:{s.toString().padStart(2, "0")} remaining
    </span>
  );
}

function StatusCard({ icon: Icon, tone, title, message }) {
  return (
    <div className="min-h-screen bg-[#05050A] text-slate-200 flex items-center justify-center px-5">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-md w-full bx-card p-8 text-center"
        data-testid="paylink-status"
      >
        <Icon size={42} className={`mx-auto mb-3 ${tone}`} />
        <h1 className="bx-heading text-2xl font-extrabold">{title}</h1>
        <p className="text-slate-400 text-sm mt-3">{message}</p>
        <p className="text-slate-500 text-xs mt-6">
          Need a fresh link? Contact <span className="text-purple-300">support@binarex.co</span>
        </p>
      </motion.div>
    </div>
  );
}

export default function PayLink() {
  const { token } = useParams();
  const [isMobile, setIsMobile] = useState(true);
  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [showQR, setShowQR] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lastUri, setLastUri] = useState(null);
  const [refId, setRefId] = useState(null);

  useEffect(() => {
    setIsMobile(isMobileDevice());
    api.get(`/upi/link/${token}`)
      .then((r) => {
        setLink(r.data);
        setName(r.data.customer_name_hint || "");
        setMobile(r.data.customer_mobile_hint || "");
      })
      .catch((e) => setErr(e?.response?.data?.detail || "This payment link could not be loaded."))
      .finally(() => setLoading(false));
  }, [token]);

  const upiUri = useMemo(() => {
    if (!link?.receiver_upi_id || !link?.amount) return "";
    const pn = encodeURIComponent(link.receiver_name || "BINAREX AND CO");
    // Inject merchant ref-id (returned by /log) into the transaction note so
    // bank statements + aggregator webhooks carry it — autoconfirm matches by
    // exact ref-id rather than only amount.
    const noteParts = [refId, link.label || `Payment from ${name || "Customer"}`].filter(Boolean);
    const tn = encodeURIComponent(noteParts.join(" — "));
    return `upi://pay?pa=${encodeURIComponent(link.receiver_upi_id)}&pn=${pn}&am=${link.amount}&cu=INR&tn=${tn}`;
  }, [link, name, refId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Please enter your name"); return; }
    if (!/^[0-9+\-\s()]{7,20}$/.test(mobile.trim())) { toast.error("Please enter a valid mobile number"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/upi/link/${token}/log`, {
        customer_name: name.trim(),
        customer_mobile: mobile.trim(),
      });
      if (data?.merchant_reference_id) setRefId(data.merchant_reference_id);
      // Build the intent URI using the just-issued ref id (don't wait for
      // upiUri's memo recompute — capture it inline).
      const refForUri = data?.merchant_reference_id || refId;
      const pn = encodeURIComponent(link.receiver_name || "BINAREX AND CO");
      const noteParts = [refForUri, link.label || `Payment from ${name.trim()}`].filter(Boolean);
      const tn = encodeURIComponent(noteParts.join(" — "));
      const uri = `upi://pay?pa=${encodeURIComponent(link.receiver_upi_id)}&pn=${pn}&am=${link.amount}&cu=INR&tn=${tn}`;
      setLastUri(uri);

      let intentLanded = false;
      const onBlur = () => { intentLanded = true; };
      const onVisibility = () => { if (document.hidden) intentLanded = true; };
      window.addEventListener("blur", onBlur, { once: true });
      document.addEventListener("visibilitychange", onVisibility, { once: true });

      window.location.href = uri;

      setTimeout(() => {
        window.removeEventListener("blur", onBlur);
        document.removeEventListener("visibilitychange", onVisibility);
        if (!intentLanded && !document.hidden) {
          setShowQR(true);
          toast("No UPI app responded — scan the QR with another phone", { icon: "📱" });
        }
        setBusy(false);
      }, 1500);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to initiate payment";
      toast.error(msg);
      // If the link became invalid mid-flight, refresh to show the status card
      if (err?.response?.status === 410) {
        try { const r = await api.get(`/upi/link/${token}`); setLink(r.data); } catch {}
      }
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#05050A] text-slate-400 flex items-center justify-center text-sm">
        <Lucide.Loader2 className="animate-spin mr-2" size={16} /> Loading payment link…
      </div>
    );
  }

  if (err || !link) {
    return <StatusCard icon={Lucide.AlertOctagon} tone="text-rose-300" title="Link not found" message={err || "This payment link does not exist or was removed."} />;
  }

  if (link.status === "paid") {
    return <StatusCard icon={Lucide.CheckCircle2} tone="text-emerald-300" title="Already paid" message={`This payment link of ₹${Number(link.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })} has already been completed. Thank you!`} />;
  }
  if (link.status === "expired") {
    return <StatusCard icon={Lucide.Hourglass} tone="text-amber-300" title="Link expired" message="This payment link has expired (24h window). Please ask for a new one." />;
  }
  if (link.status === "revoked") {
    return <StatusCard icon={Lucide.Ban} tone="text-rose-300" title="Link revoked" message="This payment link has been revoked by the merchant." />;
  }

  if (!isMobile) {
    return (
      <div className="min-h-screen bg-[#05050A] text-slate-200 flex items-center justify-center px-5">
        <div className="max-w-md w-full bx-card p-8 text-center" data-testid="paylink-desktop-blocker">
          <Lucide.Smartphone size={42} className="mx-auto text-purple-300 mb-3" />
          <h1 className="bx-heading text-2xl font-extrabold">Open on your mobile</h1>
          <p className="text-slate-400 text-sm mt-3">
            This payment link is only available on mobile devices.
          </p>
          <p className="text-slate-500 text-xs mt-4">
            Scan the QR or open this link from your phone to pay any installed UPI app — GPay, PhonePe, Paytm, BHIM and more.
          </p>
          <div className="mt-5 rounded-xl bg-white p-4 mx-auto" style={{ width: 220 }}>
            <QRCodeCanvas value={window.location.href} size={188} level="M" includeMargin={false} />
          </div>
          <div className="bx-mono text-[10px] text-slate-500 break-all mt-3">{window.location.href}</div>
        </div>
      </div>
    );
  }

  const amtFmt = Number(link.amount).toLocaleString(undefined, { minimumFractionDigits: 2 });

  return (
    <div className="min-h-screen bg-[#05050A] text-slate-100" data-testid="paylink-page">
      <Seo title={`Pay ₹${amtFmt} — BINAREX.CO`} description="Secure UPI payment for BINAREX.CO services." />
      <div
        className="px-5 pt-8 pb-6 relative overflow-hidden"
        style={{ background: "linear-gradient(180deg, rgba(124,58,237,0.25) 0%, transparent 100%)" }}
      >
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-2 mb-4">
          <div className="bx-heading text-xl font-extrabold">BINAREX.CO</div>
          <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Pay link</span>
        </motion.div>
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <Lucide.ShieldCheck size={13} className="text-emerald-300" />
          Secured payment · single-use · <Countdown expiresAt={link.expires_at} />
        </div>
      </div>

      <div className="px-5 max-w-md mx-auto">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bx-card p-5">
          {link.label && (
            <div className="mb-4">
              <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500">For</div>
              <div className="text-sm text-slate-100 mt-1" data-testid="paylink-label">{link.label}</div>
            </div>
          )}

          <div className="rounded-xl p-4 mb-5" style={{ background: "linear-gradient(135deg, rgba(139,92,246,0.18), rgba(59,130,246,0.10))", border: "1px solid rgba(139,92,246,0.25)" }}>
            <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400">You will pay</div>
            <div className="mt-1 flex items-baseline gap-2">
              <Lucide.IndianRupee size={22} className="text-purple-300" />
              <span data-testid="paylink-amount" className="bx-heading text-3xl font-extrabold text-white">{amtFmt}</span>
              <span className="text-[10px] text-slate-500 uppercase tracking-wider ml-1">Locked</span>
            </div>
            <div className="mt-3 flex items-center justify-between text-xs">
              <span className="text-slate-400">to</span>
              <div className="text-right">
                <div className="text-slate-100">{link.receiver_name || "BINAREX AND CO"}</div>
                <div className="bx-mono text-purple-300 text-[11px]" data-testid="paylink-receiver">{link.receiver_upi_id}</div>
              </div>
            </div>
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="paylink-form">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-400">Your name *</label>
              <input
                data-testid="paylink-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Full name"
                autoComplete="name"
                required
                maxLength={120}
                className="bx-input mt-1 w-full"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-400">Mobile number *</label>
              <input
                data-testid="paylink-mobile"
                type="tel"
                inputMode="tel"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                placeholder="10-digit mobile"
                autoComplete="tel"
                required
                maxLength={20}
                className="bx-input mt-1 w-full"
              />
            </div>

            <label className="flex items-start gap-3 text-sm cursor-pointer">
              <input
                type="checkbox"
                data-testid="paylink-show-qr"
                checked={showQR}
                onChange={(e) => setShowQR(e.target.checked)}
                className="mt-1"
              />
              <span className="text-slate-300">
                Show QR code
                <span className="block text-[11px] text-slate-500">Scan from another device to pay.</span>
              </span>
            </label>

            {showQR && upiUri && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="rounded-xl bg-white p-4 mx-auto"
                style={{ width: 250 }}
                data-testid="paylink-qr"
              >
                <QRCodeCanvas value={upiUri} size={218} level="M" includeMargin={false} />
              </motion.div>
            )}

            <button
              type="submit"
              data-testid="paylink-pay-now"
              disabled={busy}
              className="w-full mt-2 rounded-xl py-3.5 font-bold text-base disabled:opacity-50 flex items-center justify-center gap-2"
              style={{
                background: "linear-gradient(135deg, #8B5CF6 0%, #3B82F6 100%)",
                boxShadow: "0 12px 30px -10px rgba(139,92,246,0.6)",
              }}
            >
              {busy ? <Lucide.Loader2 className="animate-spin" size={18} /> : <Lucide.IndianRupee size={18} />}
              PAY ₹{amtFmt}
            </button>

            {lastUri && (
              <>
                {refId && (
                  <div className="text-center text-[10px] text-slate-500 mt-2" data-testid="paylink-ref-id">
                    Reference: <span className="bx-mono text-purple-300">{refId}</span>
                    <span className="text-slate-600"> — appears in your bank statement</span>
                  </div>
                )}
                <a
                  href={lastUri}
                  className="block text-center text-xs text-purple-300 underline mt-2"
                  data-testid="paylink-reopen-intent"
                >
                  Didn't open? Tap to retry payment app
                </a>
                {refId && (
                  <a
                    href={`/upi/thanks/${refId}`}
                    className="block text-center mt-3 rounded-xl py-2.5 text-xs font-semibold bg-white/5 hover:bg-white/10 text-purple-300 border border-purple-400/20"
                    data-testid="paylink-check-status"
                  >
                    <Lucide.Search size={12} className="inline mr-1" /> After paying, tap here to check status
                  </a>
                )}
              </>
            )}
          </form>

          <div className="mt-6 pt-4 border-t border-white/10 flex items-center justify-around text-[10px] text-slate-500">
            <span>GPay</span><span>·</span><span>PhonePe</span><span>·</span><span>Paytm</span><span>·</span><span>BHIM</span><span>·</span><span>Any UPI</span>
          </div>
        </motion.div>

        <p className="text-[10px] text-slate-500 text-center mt-4">
          BINAREX AND CO · Direct UPI · Single-use · Expires in 24h
        </p>
      </div>
    </div>
  );
}
